<!-- DefaultCommentEditor -->
<script lang="ts" setup>
    import { useDefaultCommentsStore } from '../../lib/Stores/DefaultCommentsStore'
    import { initPinia } from "../../lib/PiniaMgr"
    import { onMounted, computed, ref, reactive } from 'vue';
    import { MessageSection } from '../../LibMain';
    import { DisplayStyle } from '../../Enums/UiEnums';
    import { IDefaultCommentModel } from '../../lib/Api/Comments';
    import { useCommentAttributesStore } from '../../lib/Stores/CommentAttributesStore';
    import { useListStore } from '../../lib/Stores/ListStore';
    import { IAlertTreeOptionModel } from '../../lib/Models/Comments/Alerts';
    import { IUserAndGroupTableItem } from '../../lib/Models/User/UserAndGroupItem';
    import TextInput from '../../components/TextInput.vue'
    import AlertsTree from "../Comments/AlertsTree.vue"
    import OptionsDropPanel from "../../components/OptionsDropPanel.vue"
    import UsersAndGroupsTable from './UsersAndGroupsTable.vue'
    import SelectedAlertBadges from './SelectedAlertBadges.vue'
    import SelectedUsersAndGroupBadges from '../../components/SelectionIndicators/SelectedUsersAndGroupBadges.vue'
    import BillerActionSelectList from './BillerActionSelectList.vue'
    import Card from "../../components/Card.vue"    

    const props = defineProps<{
        commentId?: number,
        groupId?: number,
        defaultCommentGroupId?: number,
        showClaimCommentParts?: boolean
    }>();

    const emit = defineEmits<{
        (e: "saved", comment: IDefaultCommentModel, inserted: boolean): void,
        (e: "cancel"): void,
    }>();

    const compData = reactive<{
        alertOptions: IAlertTreeOptionModel[],

        usersAndGroups: (IUserAndGroupTableItem)[],

        openAlertOptionsTree: boolean
    }>({
        alertOptions: [],
        usersAndGroups: [],
        openAlertOptionsTree: false
    });

    const defaultCommentsStore = useDefaultCommentsStore(initPinia());
    const commentAttributesStore = useCommentAttributesStore(initPinia());
    const listStore = useListStore(initPinia());

    const commentInfo = ref<IDefaultCommentModel>({ 
        dflt_comment_id: 0, 
        dflt_comment_group_id: 0, 
        commentText:"", 
        description: "",
        alert_types: [],
        audienceIds: []
 });
    const DescriptionInput = ref();
    const CommentInput = ref();

    onMounted(async () =>{
        defaultCommentsStore.initializeDefaultGroups();

        if (props.commentId)
        {
            const commentInfoFromStore = await defaultCommentsStore.getCommentInfo(props.commentId);
            if (!commentInfoFromStore)
            {
                alert(`Unable to find data for comment (id: ${props.commentId})`);
                return;
            }

            // Clone comment data to ensure we don't update the data in the store
            commentInfo.value = {...commentInfoFromStore};
            CommentInput.value.focus();
        }
        else
        {
            if (props.defaultCommentGroupId)
            {
                commentInfo.value.dflt_comment_group_id = props.defaultCommentGroupId;
            }
            DescriptionInput.value.focus();
        }

        commentAttributesStore.getAlertTree()
            .then(resp => {
                compData.alertOptions = resp;
            });

        listStore
            .getUsersGroupsAndCommentFavoritesArray()
            .then(resp => compData.usersAndGroups = resp);
    });

    const commentGroupId = computed<number|undefined>(()=>{
        return props.groupId;
    });

    async function onSaveComment()
    {
        const inserted = !!commentInfo.value.dflt_comment_id;
        await defaultCommentsStore.saveDefaultComment(commentInfo.value);
        emit("saved", commentInfo.value, inserted);
    }

    const reasonsSaveNotAllowed = computed(() =>{
        if (!commentInfo.value.dflt_comment_group_id)
            return "Comment group not selected";
        if (!commentInfo.value.description?.length)
            return "Comment group description not entered";
        if (!commentInfo.value.commentText?.length)
            return "Comment text not entered";            
    });

    function onClickAlertItem(check: boolean, item: IAlertTreeOptionModel)
    {
        commentInfo.value.alert_types = commentInfo.value.alert_types ?? [];

        if (check)
            commentInfo.value.alert_types.push(item.id);
        else
        {
            commentInfo.value.alert_types = 
                commentInfo.value.alert_types.filter(val => val !== item.id);
        }
    }


</script>
<template>

    <div>
        <div class="mb-1">
            Associated Default Comment Group
            <select v-model="commentInfo.dflt_comment_group_id" class="form-select">
                <option :value="group.dflt_comment_group_id" v-for="group in defaultCommentsStore.defaultCommentGroups" >
                    {{group.description}}
                </option>
            </select>
        </div>

        <div class="mb-1">
            Description
            <TextInput ref="DescriptionInput" v-model:textValue="commentInfo.description" :maxLength="20" />
        </div>

        <div class="mb-2">
            Comment Text
            <TextInput ref="CommentInput" :rows="4" v-model:textValue="commentInfo.commentText" :maxLength="2500"/>
        </div>

        <Card class="mb-1" :headerDisplayStyle="DisplayStyle.Secondary">
            <template #title>Default Alerts for Patient Comments</template>
            <div class="mb-1">
                <OptionsDropPanel :isOpen="compData.openAlertOptionsTree">
                    <template #title>
                        Alert
                    </template>
                    <template #options>
                        <AlertsTree 
                            :alertOptions="compData.alertOptions"
                            @changeAlertSelection="(checked, treeItem) => onClickAlertItem(checked, treeItem)"
                            :selectedAlertOptions="commentInfo.alert_types"
                            @update:selectedAlertOptions="val => commentInfo.alert_types = val" />
                        <hr/>
                    </template>
                    <template #summary>
                        <SelectedAlertBadges :alertIds="commentInfo.alert_types" />
                    </template>
                </OptionsDropPanel>
            </div>
    
            <div class="mb-1">
                <OptionsDropPanel>
                    <template #title>
                        Users/Groups
                    </template>
                    <template #options>
                        <UsersAndGroupsTable 
                            :usersAndGroups="compData.usersAndGroups" 
                            v-model:selectedusersAndGroupsIds="commentInfo.audienceIds" />
                        <hr/>
                    </template>
                    <template #summary>
                        <SelectedUsersAndGroupBadges 
                            :usersAndGroups="compData.usersAndGroups"
                            :selectedUsersAndGroups="commentInfo.audienceIds"
                            :nothingSelectedText="'All'" />
                    </template>
                </OptionsDropPanel>
            </div>
        </Card>

        <Card class="mb-1" :headerDisplayStyle="DisplayStyle.Secondary" v-if="props.showClaimCommentParts">
            <template #title>Default Alerts for Claim Comments</template>
            <div>
                Biller Action
                <BillerActionSelectList v-model:billerActionId="commentInfo.biller_action_id" />
            </div>
        </Card>


        <slot name="ActionSection">
            <div class="mt-1">
                <MessageSection v-if="reasonsSaveNotAllowed" :displayStyle="DisplayStyle.Warning">
                    {{ reasonsSaveNotAllowed }}
                </MessageSection>
                <button class="btn btn-primary" @click="onSaveComment()" v-if="!reasonsSaveNotAllowed">
                    Save
                </button>

                <button class="btn btn-danger ms-1" @click="emit('cancel')">
                    Cancel
                </button>        
            </div>
        </slot>
    </div>
</template>